import { useState, useEffect } from "react";
import styles from "./Notifications.module.css";

// Utility function to parse the custom date string "DD-MM-YYTHH:MM:SS"
const parseCustomDate = (dateStr) => {
  const [datePart, timePart] = dateStr.split("T");
  const [day, month, year] = datePart.split("-").map(Number);
  const [hours, minutes, seconds] = timePart.split(":").map(Number);
  // Assuming YY is 20YY for years like 25 -> 2025
  const fullYear = year >= 0 && year <= 99 ? 2000 + year : year;
  return new Date(fullYear, month - 1, day, hours, minutes, seconds);
};

// Utility to get date key for grouping (DD-MM-YY)
const getDateKey = (dateStr) => {
  return dateStr.split("T")[0]; // Just the date part
};

// Utility to format date key for display as "27Oct, 2025"
const formatDateForHeader = (dateKey) => {
  const [day, month, year] = dateKey.split("-").map(Number);
  const fullYear = 2000 + year;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthAbbr = monthNames[month - 1];
  return `${day}${monthAbbr}, ${fullYear}`;
};

// Utility to format time display based on category
const formatTime = (item, heading) => {
  const itemDate = parseCustomDate(item.date);
  const now = new Date(); // Simulated current time: Oct 31, 2025, 2:30 PM

  if (heading === "Today") {
    const diffMs = now - itemDate;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60 && diffMin >= 0) {
      return `${diffMin} min`;
    } else {
      return itemDate
        .toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase();
    }
  } else {
    // For Yesterday or Remaining: just the time
    return itemDate
      .toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  }
};

// Updated categorize function to group remaining by date
const categorizeNotifications = (notifications, currentDate) => {
  const now = new Date(currentDate);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1); // Start of yesterday

  const todayNotifications = [];
  const yesterdayNotifications = [];
  const remainingGroups = new Map(); // Map<dateKey, array of notifications>

  notifications.forEach((item) => {
    const itemDate = parseCustomDate(item.date);
    const itemStartOfDay = new Date(
      itemDate.getFullYear(),
      itemDate.getMonth(),
      itemDate.getDate()
    );
    const dateKey = getDateKey(item.date);

    if (itemStartOfDay.getTime() === today.getTime()) {
      todayNotifications.push(item);
    } else if (itemStartOfDay.getTime() === yesterday.getTime()) {
      yesterdayNotifications.push(item);
    } else {
      if (!remainingGroups.has(dateKey)) {
        remainingGroups.set(dateKey, []);
      }
      remainingGroups.get(dateKey).push(item);
    }
  });

  // Sort each array/group by parsed date descending (newest first)
  todayNotifications.sort(
    (a, b) => parseCustomDate(b.date) - parseCustomDate(a.date)
  );
  yesterdayNotifications.sort(
    (a, b) => parseCustomDate(b.date) - parseCustomDate(a.date)
  );

  // For remaining, sort groups by date descending, and sort items within each group
  const sortedRemainingGroups = Array.from(remainingGroups.entries())
    .sort(([dateKeyA], [dateKeyB]) => {
      const dateA = parseCustomDate(dateKeyA + "T00:00:00");
      const dateB = parseCustomDate(dateKeyB + "T00:00:00");
      return dateB - dateA; // Descending
    })
    .map(([dateKey, items]) => {
      items.sort((a, b) => parseCustomDate(b.date) - parseCustomDate(a.date));
      return { dateKey, items };
    });

  return {
    today: todayNotifications,
    yesterday: yesterdayNotifications,
    remaining: sortedRemainingGroups, // Array of {dateKey, items}
  };
};

// Updated Notifications component
const Notifications = () => {
  const [categorizedData, setCategorizedData] = useState({
    today: [],
    yesterday: [],
    remaining: [],
  });
  const currentDate = new Date(); // With time for relative calculations

  const AlertNotifications = [
    "Insufficient CUPI Balance",
    "Low CUPI Balance",
    "You have outstanding dues",
    "CUPI is temporarily down",
    "You are about to finish your Rs 1,00,000 spend limit ",
  ];
  const RejectNotification = [
    "Rs 1,00,000 Spend limit reached",
    "User KYC rejected",
    "Fund transfer failed",
  ];
  const AcceptNotification = [
    "Advanced KYC done!",
    "User KYC done!",
    "Your Bank Account has been validated for CUPI",
    "Your Expense Recovery Report is Ready",
    "Outstanding dues cleared!",
    "Your CUPI wallet has been loaded",
  ];
  // Simulate API call - replace with your actual fetch/useEffect
  useEffect(() => {
    const apiData = [
      {
        title: "Low CUPI Balance",
        body: "You have Outstanding dues. To unblock spending with CUPI and adding users, please load money into CUPI and clear your dues now.",
        date: "30-10-25T00:00:32",
      },
      {
        title: "Advanced KYC done!",
        body: "this is a test",
        date: "30-10-25T00:00:32",
      },
      {
        title: "hello",
        body: "this is a test",
        date: "30-10-25T00:00:32",
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Rs 1,00,000 Spend limit reached",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Outstanding dues cleared!",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Your Bank Account has been validated for CUPI",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "Accept",
        body: "You're Hired",
        date: "31-10-25T00:00:32", // Today, older
      },
      {
        title: "hello",
        body: "this is a test recent",
        date: "31-10-25T13:57:32", // Today, 13 min ago
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "30-10-25T00:00:32", // Yesterday
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "29-10-25T00:00:32", // Remaining: 29-10-25
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "29-10-25T00:00:32", // Remaining: 29-10-25
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "28-10-25T00:00:32", // Remaining: 28-10-25
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "28-10-25T00:00:32", // Remaining: 28-10-25
      },
      {
        title: "heyy",
        body: "You're Hired",
        date: "27-10-25T00:00:32", // Remaining: 27-10-25
      },
    ];
    const categorized = categorizeNotifications(apiData, currentDate);
    setCategorizedData(categorized);
  }, []);

  // Render helper for a section (today, yesterday, or a remaining group)
  const renderSection = (items, heading) => (
    <div className={styles.innerContainer}>
      <div className={styles.innerContainerHeading}>
        <div className={styles.innerContainerHeadingText}>{heading}</div>
      </div>
      {items.map((item, index) => (
        <div
          key={`${heading}-${index}`}
          className={styles.NotificationContainer}
        >
          <div className={styles.NotificationInner1}>
            <img
              className={styles.ContainerWarningIcon}
              alt=""
              src={
                AlertNotifications.includes(item.title)
                  ? "/CupiAdminAlert.svg"
                  : AcceptNotification.includes(item.title)
                  ? "/CupiAdminSucces.svg"
                  : RejectNotification.includes(item.title)
                  ? "/CupiAdminFailed.svg"
                  : "/CupiAdminicon.svg"
              }
            />
          </div>
          <div className={styles.NotificationInner2}>
            <div className={styles.NotificationInnerMessage}>{item.body}</div>
            <div className={styles.NotificationInnerTime}>
              {formatTime(item, heading)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const { today, yesterday, remaining } = categorizedData;

  return (
    <div className={styles.Container}>
      {today.length > 0 && renderSection(today, "Today")}
      {yesterday.length > 0 && renderSection(yesterday, "Yesterday")}
      {remaining.map(({ dateKey, items }) =>
        renderSection(items, formatDateForHeader(dateKey))
      )}
    </div>
  );
};

export default Notifications;
